import _ from "lodash"

import { getExtraWithID } from "./extras-selector"
import { toData } from "../../utils/string_utils"

// Grabs the first overridden device from extras if it exists
const getDeviceFromExtras = state => {
  const { extras } = state.basket
  const extraIDWithDevice = extras.find(
    extraID => "deviceOverride" in getExtraWithID(extraID)(state)
  )

  return (
    extraIDWithDevice && getExtraWithID(extraIDWithDevice)(state).deviceOverride
  )
}

// Get the relevant device
// Using the correct hierarchy of devices
// Extras -> Plan -> Device
const getDeviceToUse = state => {
  const { phoneID, planID } = state.basket
  const defaultDevice = state.devices.byID[phoneID]
  const extraDevice = getDeviceFromExtras(state)
  const planDevice = state.plans.byID[planID].deviceOverride

  return extraDevice || planDevice || defaultDevice
}

export const getDeviceText = state => getDeviceToUse(state).name

export const getDeviceImage = state => getDeviceToUse(state).img

export const getDeviceDetails = state => getDeviceToUse(state).details

const getNormalPlanText = (planID, state) => {
  const {
    minutes,
    texts,
    data,
    contractLength,
    network,
    speed
  } = state.plans.byID[planID].data

  let planText = `${contractLength} months`
  if (data) {
    planText += `${toData(data)}`
  }
  if (network) {
    planText += `${network}`
  }
  if (minutes) {
    planText += `${minutes} minutes`
  }
  if (texts) {
    planText += `${texts} texts`
  }

  if (speed) {
    planText += `.<br />${speed}.`
  }

  return planText
}

const getBuiltPlanText = builtPlan => {
  // Assume minutes is unlimited
  const minutes = "unlimited"
  const texts = "unlimited"
  const {
    duration,
    data: { value: dataValue }
  } = builtPlan

  return `${duration} months, ${toData(
    dataValue
  )} data, ${minutes} minutes, ${texts} texts`
}

export const getPlanText = state => {
  const { planID, builtPlan } = state.basket

  return builtPlan
    ? getBuiltPlanText(builtPlan)
    : getNormalPlanText(planID, state)
}

const getPlanCostsForDevice = (phoneID, planID, state) => {
  const { builtPlan } = state.basket

  return builtPlan
    ? {
        upfront: builtPlan.upfront,
        handset: builtPlan.cost
      }
    : state.plans.byID[planID].devices[phoneID]
}

// Get details on the phone in the basket
export const getDeviceSummaryDetails = state => {
  const { phoneID, planID } = state.basket
  const { name, img, costs } = getDeviceToUse(state)

  const planCosts = getPlanCostsForDevice(phoneID, planID, state)

  // Calculate the costs, by taking the phone costs (if it exists) and
  // adding the upfront plan cost
  let totalCosts = costs
    ? {
        ...costs,
        upfront: _.get(costs, "upfront", 0) + planCosts.upfront,
        monthly: _.get(costs, "maf", 0) + planCosts.handset
      }
    : {
        upfront: planCosts.upfront,
        monthly: planCosts.handset
      }

  if (state.plans.options.excludeVAT) {
    totalCosts = _.clone(totalCosts)
    Object.keys(totalCosts).map(
      costName =>
        (totalCosts[costName] =
          totalCosts[costName] / state.plans.options.vatDivider)
    )
  }

  return {
    text: name,
    img,
    costs: totalCosts
  }
}

const getNormalPlanSummaryDetails = (phoneID, planID, state) => {
  const thisPlan = state.plans.byID[planID]

  // grab the planCosts, ignoring the upfront cost since that's in the phone detail line
  let { upfront: _, ...planCosts } = thisPlan.devices[phoneID]

  const {
    minutes,
    name,
    postName,
    tagline,
    texts,
    // data,
    contractLength,
    network,
    speed,
    speedDescription
  } = thisPlan.data

  let planText = ""

  if (speedDescription) {
    planText += `${speedDescription}<br/>`
  }
  if (name) {
    planText += `${name}`
    if (postName) {
      planText += ` ${postName}`
    }
  }

  if (tagline) {
    planText += `<br/><span class='normal'>${tagline}</span>`
  }
  // if (data) {
  //   planText += `${toData(data)}`
  // }

  if (network) {
    planText += `${network} data <br/>`
  }

  if (minutes) {
    planText += `${minutes} minutes <br/>`
  }
  if (texts) {
    planText += `${texts} texts`
  }

  if (speed) {
    planText += `.<br/>${speed}.`
  }

  let monthlyCost = planCosts.airtime || planCosts.maf

  if (state.plans.options.excludeVAT) {
    monthlyCost = monthlyCost / state.plans.options.vatDivider
  }

  let planEntertainmentCost = 0

  if (planID in state.basket.entertainment) {
    planEntertainmentCost = state.basket.entertainment[planID]
  }

  let entertainmentBenefit = thisPlan.benefits.find(
    elem => elem.indexOf("ENTERTAINMENT_PROVIDERS") > -1
  )

  let entertainmentDesc = entertainmentBenefit
    ? state.benefits.byID[entertainmentBenefit]
    : ""

  let benefitsText = thisPlan.benefits.map(
    benefitID => state.benefits.byID[benefitID].mainText
  )

  return {
    duration: contractLength,
    text: planText,
    benefitsText,
    costs: {
      monthly: monthlyCost
    },
    entertainmentCost: planEntertainmentCost,
    entertainmentDetails: entertainmentDesc
  }
}

const getBuiltPlanSummaryDetails = builtPlan => {
  // grab the planCosts, ignoring the upfront cost since that's in the phone detail line
  let {
    upfront,
    duration,
    data: { value: data },
    cost: monthlyCost
  } = builtPlan

  const minutes = "unlimited"
  const texts = "unlimited"

  let planText = ""

  planText += `${toData(data)} data<br/>${minutes} minutes<br/>${texts} texts`

  return {
    duration,
    text: planText,
    costs: {
      upfront: upfront,
      monthly: monthlyCost
    }
  }
}

// Get details on the plan in the basket
export const getPlanSummaryDetails = state => {
  const { phoneID, planID, builtPlan } = state.basket

  return builtPlan
    ? getBuiltPlanSummaryDetails(builtPlan)
    : getNormalPlanSummaryDetails(phoneID, planID, state)
}

// Get the list of extras in the basket
export const getExtrasSummaryDetails = state => {
  const { extras } = state.basket

  return extras.map(extraID => {
    const extraDetail = getExtraWithID(extraID)(state)

    return {
      text: extraDetail.shortText,
      costs: {
        monthly: extraDetail.cost
      }
    }
  })
}

// A helper function to add up the costs
const addCosts = (costA, costB) => ({
  ...costA,
  ...Object.keys(costB).reduce(
    (agg, costType) => ({
      ...agg,
      [costType]: _.get(costA, costType, 0) + costB[costType]
    }),
    {}
  )
})

// Calculate the costs
export const getTotalCosts = state => {
  let totalCosts = {}
  // Get the phone
  // If it has costs, let's get them
  const phoneID = state.basket.phoneID
  if (!phoneID) {
    // no phone, so quit out
    return totalCosts
  }
  const phoneData = state.devices.byID[phoneID]
  if (!phoneData) {
    return totalCosts
  }

  if (phoneData.costs) {
    totalCosts = addCosts(totalCosts, phoneData.costs)
  }

  const planID = state.basket.planID

  // Do built plan costs if it exist
  if (state.basket.builtPlan) {
    totalCosts = addCosts(totalCosts, {
      upfront: state.basket.builtPlan.upfront,
      monthly: state.basket.builtPlan.cost
    })
  } else {
    // Get the plan
    // If it has costs, let's get them and total
    if (!planID) {
      // no plan, so quit out here
      return totalCosts
    }
    const planCosts = state.plans.byID[planID].devices[phoneID]

    totalCosts = addCosts(totalCosts, planCosts)

    if (totalCosts.airtime)
      totalCosts.monthly = totalCosts.airtime + totalCosts.handset
    else totalCosts.monthly = totalCosts.maf

    if (state.plans.options.excludeVAT) {
      totalCosts = _.clone(totalCosts)
      Object.keys(totalCosts).map(
        costName =>
          (totalCosts[costName] =
            totalCosts[costName] / state.plans.options.vatDivider)
      )
    }
  }

  // Go through the extras
  // Add their costs if they exist
  totalCosts = state.basket.extras.reduce((totalCosts, extraID) => {
    return addCosts(totalCosts, {
      monthly: getExtraWithID(extraID)(state).cost
    })
  }, totalCosts)

  if (state.plans.options.excludeVAT) {
    const mafWithoutVAT = totalCosts.monthly || 0
    const maftWithVAT = mafWithoutVAT * state.plans.options.vatDivider
    totalCosts.vat = maftWithVAT - mafWithoutVAT
  }

  if (planID) {
    const addedEntertainment = Object.keys(state.basket.entertainment).filter(
      entertainmentPlan => entertainmentPlan === planID
    )
    const entertainmentCost = state.basket.entertainment[addedEntertainment[0]]
    if (entertainmentCost) {
      totalCosts.monthly += entertainmentCost
    }
  }

  // return a costs object
  return totalCosts
}

export const hasSwappableBenefits = state => {
  const { planID, builtPlan } = state.basket

  if (builtPlan) {
    return false
  }

  const swappableBenefits = state.plans.byID[planID].swappableBenefits

  if (!swappableBenefits) {
    return false
  }

  return swappableBenefits.benefits && swappableBenefits.benefits.length > 0
}

export const getSwappableBenefits = state => {
  const { planID, builtPlan } = state.basket

  if (builtPlan) {
    return null
  }

  if (!state.plans.byID[planID].swappableBenefits) {
    return null
  }

  let planSwapapbleBenefits =
    state.plans.byID[planID].swappableBenefits.benefits

  let shownSwappableBenefits = []
  planSwapapbleBenefits.map(
    benefitID =>
      (shownSwappableBenefits[benefitID] = state.benefits.byID[benefitID])
  )
  return shownSwappableBenefits
}

export const getAllPlansButtonClicked = state => {
  return state.basket.allPlansButtonClicked
}

export const planHasHalo3 = state => {
  return !!state.plans.byID[state.basket.planID].data.hasHalo3
}

export const isHaloExtraAdded = state => {
  return _.includes(state.basket.extras, "HALO_PREMIUM")
}
